import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateEmployeeRequest, Employee, UpdateEmployeeRequest } from '@mervonn/shared-types';
import { employeesApi, employeeName, type ListEmployeesQuery } from './api';

const keys = {
  all: ['employees'] as const,
  list: ['employees', 'list'] as const,
  listPaged: (query: ListEmployeesQuery) => ['employees', 'list-paged', query] as const,
  detail: (id: string) => ['employees', 'detail', id] as const,
  formMetadata: ['employees', 'form-metadata'] as const,
};

export function useEmployees(enabled = true) {
  return useQuery({ queryKey: keys.list, queryFn: employeesApi.list, enabled, staleTime: 60_000 });
}

/** Stage 4 Phase 2 — real pagination, for `EmployeesListPage` only. Every
 * other consumer of this module (Leave/Attendance/Payroll/Reports dropdowns)
 * wants the full roster and keeps using `useEmployees` above. */
export function useEmployeesPaged(query: ListEmployeesQuery) {
  return useQuery({
    queryKey: keys.listPaged(query),
    queryFn: () => employeesApi.listPaged(query),
    staleTime: 30_000,
  });
}

export function useEmployeeStats(branchId?: string, enabled = true) {
  return useQuery({
    queryKey: ['employees', 'stats', branchId ?? 'all'] as const,
    queryFn: () => employeesApi.stats(branchId),
    enabled,
    staleTime: 30_000,
  });
}

export function useCurrentShift(id: string, enabled = true) {
  return useQuery({ queryKey: ['employees', 'current-shift', id] as const, queryFn: () => employeesApi.currentShift(id), enabled });
}

export function useSyncAllDevices(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => employeesApi.syncAllDevices(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['devices'] }),
  });
}

export function useEmployee(id: string) {
  return useQuery({ queryKey: keys.detail(id), queryFn: () => employeesApi.get(id) });
}

export function useEmployeeFormMetadata(enabled: boolean) {
  return useQuery({
    queryKey: keys.formMetadata,
    queryFn: employeesApi.formMetadata,
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useCreateEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateEmployeeRequest) => employeesApi.create(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.all }),
  });
}

/** Re-design T09 — a dry run writes nothing; a commit refreshes the roster and the setup status. */
export function useImportEmployees() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, mode }: { file: File; mode: 'dry_run' | 'commit' }) => employeesApi.importCsv(file, mode),
    onSuccess: (res) => {
      if (res.mode !== 'commit') return;
      void qc.invalidateQueries({ queryKey: keys.all });
      void qc.invalidateQueries({ queryKey: ['setup-status'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useUpdateEmployee(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateEmployeeRequest) => employeesApi.update(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.all }),
  });
}

export function useDeactivateEmployee(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => employeesApi.deactivate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.all }),
  });
}

export function useArchiveEmployee(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => employeesApi.archive(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.all }),
  });
}

export function useRestoreEmployee(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => employeesApi.restore(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.all }),
  });
}

/** id → display name, for screens (e.g. payroll) that only carry ids.
 * Backed by the same cached list query. */
export function useEmployeeNames(enabled: boolean) {
  const query = useEmployees(enabled);
  const byId = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of query.data ?? []) m.set(e.employeeId, employeeName(e));
    return m;
  }, [query.data]);
  return { byId, isLoading: query.isLoading, available: enabled && !query.isError };
}

export type { Employee };
